/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { AuthenticationService } from './authentication.service';


@Injectable()
export class RestService {

  constructor(
    private mHttpClient: HttpClient,
    private mRouter: Router,
    private mAuthenticationService: AuthenticationService
  ) { }

  /**
   * Performs a GET REST Request against the given URL.  This 
   * parses the body as the <T> class. 
   * 
   * Optionally passing the Response Type for the Request so that a 
   * String can be returned / parsed. (Use 'text' for that)
   */
  public async get<T>(url: string, responseType?: string): Promise<T> {
    const httpOptions = this.createBasicHttpOptions(responseType);

    return this.mHttpClient
      .get<T>(url, httpOptions)
      .toPromise()
      .catch(error => this.handleError(error));
  }

  public async post<T>(url: string, data: any, responseType?: string): Promise<T>{
    const httpOptions = this.createBasicHttpOptions(responseType);

    return this.mHttpClient
      .post<T>(url, data, httpOptions)
      .toPromise()
      .catch(error => this.handleError(error));
  }

  public async put<T>(url: string, data: any): Promise<T>{
    const httpOptions = this.createBasicHttpOptions();

    return this.mHttpClient
      .put<T>(url, data, httpOptions)
      .toPromise()
      .catch(error => this.handleError(error));
  }

  public async upload<T>(url: string, formData: FormData): Promise<T> {
    const httpOptions = this.createUploadHttpOptions();

    return this.mHttpClient
      .post<T>(url, formData, httpOptions)
      .toPromise()
      .catch(error => this.handleError(error));
  }

  public async delete<T>(url: string, body?: any): Promise<T> {
    const httpOptions = this.createDeleteHttpOptions(body);

    return this.mHttpClient
      .delete<T>(url, httpOptions)
      .toPromise()
      .catch(error => this.handleError(error));
  }






  /**
   * Creates / Returns the HttpOptions for for the HTTP Requests.
   */
  createBasicHttpOptions(responseType?: string): HttpOptions {
    const headers = new HttpHeaders({
      'Accept': 'application/json',
      'Authorization': this.mAuthenticationService.getToken(),
      'Content-Type': 'application/json'
    });
    if (responseType?.length > 0) {
      return { headers: headers , responseType: responseType } as HttpOptions;
    } else {
      return { headers: headers } as HttpOptions;
    }
  }

  createUploadHttpOptions(): HttpOptions {
    const headers = new HttpHeaders({
      'Accept': 'application/json',
      'Authorization': this.mAuthenticationService.getToken()
    });
    return { headers: headers } as HttpOptions;
  }

  createDeleteHttpOptions(body?: any): HttpOptions {
    const headers = new HttpHeaders({
      'Accept': 'application/json',
      'Authorization': this.mAuthenticationService.getToken(),
      'Content-Type': 'application/json'
    });

    if (body != null) {
      return { headers: headers, body: body } as HttpOptions;
    } else {
      return { headers: headers } as HttpOptions;
    }
  }


  handleError(error: any): Promise<any> {
    const response: Response = error;
    
    //401 = Unauthorized.  If an Unauthorized response is returned... The Token must be invalid.  Redirect the User to the login page
    if (response.status == 401) {
      console.error("Unauthorized HTTP Status detected... Token must of expired!  Redirecting to login...");
      this.mAuthenticationService.logout();
      this.mRouter.navigate(['/login'], {queryParams: {returnUrl: this.mRouter.url}});
      return Promise.reject(null);
    }
    
    //Any other type of error return to the caller for appropriate handling
    return Promise.reject(error);
  }

}

/**
 * Interface for the HTTP Options for the REST requests.
 */
export interface HttpOptions {
  /** HTTP Headers that need to be added to the request */
  headers?: HttpHeaders;

  /** HTTP Body that needs to be added to the request */
  body?: any;

  /** HTTP Response Type for the request. (Used to allow parsing REST requests that return a String, use 'text' in that case) */
  responseType: 'json';
}
